import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import type { ApiFailure, ApiResult, Schema } from '@/api/client'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { LIMITS, bodyOf, draftOf, limitError } from './limits'
import type { Thresholds } from './limits'

/**
 * The quality gate's thresholds, as one form used by the Settings panel and
 * the wizard step.
 *
 * Settings consolidation, batch 2 (2026-09-15): moved from `pages/seo/ContentPipeline`,
 * where *Where the gate is set* sat open under the board. Organisation-wide, as
 * `read_quality` says — an editorial standard is a property of who is
 * publishing rather than of which site.
 */
export function ContentQualityForm({
  stored,
  onSubmit,
  submitLabel,
  disabled = false,
}: {
  stored: Thresholds
  onSubmit: (body: Schema<'QualityThresholdsIn'>) => Promise<ApiResult<unknown>>
  submitLabel: string
  disabled?: boolean
}): JSX.Element {
  const [draft, setDraft] = useState(draftOf(stored))
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [busy, setBusy] = useState(false)
  const invalid = LIMITS.some((limit) => limitError(limit, draft[limit.name]) !== undefined)

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (invalid) return
    setBusy(true)
    const result = await onSubmit(bodyOf(draft))
    setBusy(false)
    if (!result.ok) setFailure(result.failure)
  }

  return (
    <form className="ar-stack" aria-label="Quality gate" onSubmit={(event) => void submit(event)}>
      {LIMITS.map((limit) => (
        <Field
          key={limit.name}
          label={limit.label}
          help={limit.help}
          error={limitError(limit, draft[limit.name])}
        >
          {(aria) => (
            <input
              className="ar-input"
              {...aria}
              inputMode="numeric"
              disabled={disabled}
              value={draft[limit.name]}
              onChange={(event) => {
                setFailure(null)
                setDraft({ ...draft, [limit.name]: event.currentTarget.value })
              }}
            />
          )}
        </Field>
      ))}
      <label className="app-pub__switch">
        <input
          type="checkbox"
          checked={draft.block_avoided_words}
          disabled={disabled}
          onChange={(event) => {
            setFailure(null)
            setDraft({ ...draft, block_avoided_words: event.currentTarget.checked })
          }}
        />
        <span>
          <strong>Block a draft that uses a word on your avoid list</strong>
          <span className="ar-subtle">
            Your brand voice holds the list. Off, a forbidden word is reported and the draft still
            reaches you.
          </span>
        </span>
      </label>

      <InlineError failure={failure} />

      <div className="ar-row">
        <span className="ar-card__spacer" />
        <Button
          type="submit"
          tone="primary"
          disabled={disabled || invalid}
          busy={busy}
          busyLabel="Saving"
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
